import React, { useEffect, useState } from "react";
import { Table, Pagination } from "antd";
import { DatabaseOutlined } from "@ant-design/icons";
import InventoryMobileCard from "./InventoryMobileCard";
import useInfiniteScroll from "./useInfiniteScroll";
import useWindowSize from "./useWindowSize";
import styles from "./Inventory.module.css";
import DashboardTableHeaderStyles from "./DashboardTableHeaderStyles";

const InventoryTable = ({
  columns = [],
  dataSource = [],
  rowKey = "id",
  loading = false,
  tableClassName = "mr-table",
  rowClassName,
  tableEnter = false,
  emptyText,

  primaryFields = [],
  secondaryFields = [],
  renderCell,

  pagination = true,
  pageSize: defaultPageSize = 20,
  pageSizeOptions = ["10", "20", "50", "100"],
  mobilePageSize = 20,
  maxHeight,
  saleCode,
  saleCode0 = false,

  /* keep the table header pinned while the rows scroll (default: on) */
  stickyHeader = true,

  /* optional callback — fires whenever the visible page slice changes */
  onPageDataChange,
}) => {
  const { isMobile } = useWindowSize();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [dataSource]);

  // Infinite scroll for mobile
  const { visibleData, sentinelRef, hasMore } = useInfiniteScroll(
    dataSource,
    mobilePageSize,
  );

  const emptyNode = (
    <div className={styles.emptyState}>
      <DatabaseOutlined className={styles.emptyIcon} />
      <div className={styles.emptyText}>
        {emptyText ?? "No records to display"}
      </div>
    </div>
  );
  /* ── DESKTOP / TABLET: paginated slice (or full list when pagination is off) ── */
  const pagedData = pagination
    ? dataSource.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : dataSource;

  // Notify parent whenever the visible slice changes
  useEffect(() => {
    onPageDataChange?.(pagedData);
  }, [currentPage, pageSize, dataSource]);

  /* ── MOBILE: card list + infinite scroll ── */
  if (isMobile) {
    return (
      <div className={styles.mobileList}>
        {dataSource.length === 0 ? (
          emptyNode
        ) : (
          <>
            {visibleData.map((record, idx) => (
              <InventoryMobileCard
                key={record[rowKey] ?? idx}
                record={record}
                primaryFields={primaryFields}
                secondaryFields={secondaryFields}
                renderCell={renderCell}
              />
            ))}

            {/* Sentinel — IntersectionObserver watches this */}
            <div ref={sentinelRef} className={styles.sentinel}>
              {hasMore && (
                <div className={styles.loadingMore}>
                  <span className={styles.loadingDot} />
                  <span className={styles.loadingDot} />
                  <span className={styles.loadingDot} />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.tableCard}>
      {/* Sub-header: record count + pagination */}
      <div className={styles.tableSubHeader}>
        <span className={styles.tableInfo}>
          {dataSource.length > 0 ? (
            pagination ? (
              <>
                Showing{" "}
                <strong>
                  {Math.min(
                    (currentPage - 1) * pageSize + 1,
                    dataSource.length,
                  )}
                </strong>
                –
                <strong>
                  {Math.min(currentPage * pageSize, dataSource.length)}
                </strong>{" "}
                of <strong>{dataSource.length}</strong> records
              </>
            ) : (
              <>
                Showing all <strong>{dataSource.length}</strong> records
              </>
            )
          ) : (
            "No data loaded"
          )}
        </span>
        {pagination && (
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={dataSource.length}
            onChange={(p, s) => {
              setCurrentPage(p);
              setPageSize(s);
            }}
            pageSizeOptions={pageSizeOptions}
            showSizeChanger
            size="small"
            style={{ fontSize: 13 }}
          />
        )}
      </div>

      {/* Table */}
      <div
        className={`${styles.tableWrap} ${tableEnter ? styles.tableEnter : ""} ${
          stickyHeader ? "invStickyHead" : ""
        }`}
        style={maxHeight ? { maxHeight } : undefined}
      >
        <DashboardTableHeaderStyles saleCode={saleCode} value={saleCode0}>
          <Table
            className={tableClassName}
            size="small"
            columns={columns}
            dataSource={pagedData}
            rowKey={rowKey}
            pagination={false}
            loading={loading}
            rowClassName={(record, index) => {
              const baseClass =
                index % 2 === 0 ? styles.rowEven : styles.rowOdd;
              if (typeof rowClassName === "function") {
                const custom = rowClassName(record, index);
                return custom ? `${baseClass} ${custom}` : baseClass;
              }
              return rowClassName ? `${baseClass} ${rowClassName}` : baseClass;
            }}
            locale={{ emptyText: emptyNode }}
          />
        </DashboardTableHeaderStyles>
      </div>

      {/* Sticky table header (pins to the top of the scrolling table area) */}
      {stickyHeader && (
        <style>{`
          .invStickyHead .ant-table-thead > tr > th {
            position: sticky !important;
            top: 0;
            z-index: 4;
          }
          /* fixed (left/right) header cells keep their side offset and stay on top */
          .invStickyHead .ant-table-thead > tr > th.ant-table-cell-fix-left,
          .invStickyHead .ant-table-thead > tr > th.ant-table-cell-fix-right {
            z-index: 5;
          }
        `}</style>
      )}
    </div>
  );
};

export default InventoryTable;
